import { Group, Stack, Text, useMantineTheme } from '@mantine/core';

import { IconChevronDown } from '@tabler/icons-react';
import { Trans, useTranslation } from 'react-i18next';

import { Select } from '../../../../../../shared/components/UI/Select/Select';

import type { FileWithPathAndType } from '../../../../../../shared/api/magellan/bidResponses/dto';
import type {
  BidResponseDocument,
  BidResponseDocumentType,
} from '../../../../../../shared/entities/BidResponseDocument';
import {
  BlueHighlight,
  DocumentInput,
} from '../../../../../documents/components/DocumentInput';
import DocumentItem from '../../../../../documents/components/DocumentItem';
import { useDocumentSidePanel } from '../../../../../documents/hooks/useDocumentSidePanel';
import {
  useGetBidResponseByIdQuery,
  useUpdateBidResponseMutation,
} from '../../hooks/bidResponse.hook';
import {
  useFindAllArchivedBidResponseDocumentsQuery,
  useUpdateBidResponseDocumentMutation,
} from '../../hooks/bidResponseDocument.hook';
import {
  DocumentStatusSelectType,
  useDocumentStatus,
} from '../../hooks/useDocumentStatus';

export function BidResponseDocuments({
  bidResponseId,
}: {
  bidResponseId: number;
}) {
  const { t } = useTranslation(['bidResponse']);
  const theme = useMantineTheme();
  const { documentStatusSelectTypeLabel, selectedView, handleViewChange } =
    useDocumentStatus();

  return (
    <Stack spacing="02">
      <Group px="03" py="02" spacing="02" position="apart">
        <Text variant="sm" fw={500}>
          {t('vault.sidepanels.bidResponse.associatedDocuments')} :
        </Text>
        <Select
          variant="unstyled"
          w="170px"
          data={documentStatusSelectTypeLabel}
          value={selectedView}
          onChange={handleViewChange}
          size="xs"
          clearable={false}
          rightSection={
            <IconChevronDown size={14} color={theme.colors.gray[6]} />
          }
        />
      </Group>
      <BidResponseDocumentList
        selectedView={selectedView}
        bidResponseId={bidResponseId}
      />
    </Stack>
  );
}

const BidResponseDocumentList = ({
  selectedView,
  bidResponseId,
}: {
  selectedView: DocumentStatusSelectType;
  bidResponseId: number;
}) => {
  const updateBidResponseMutation = useUpdateBidResponseMutation(bidResponseId);
  const handleDrop = (
    files: FileWithPathAndType<BidResponseDocumentType>[],
  ) => {
    updateBidResponseMutation.mutate({ files });
  };

  if (selectedView === DocumentStatusSelectType.ARCHIVED) {
    return <ArchivedDocumentList bidResponseId={bidResponseId} />;
  }

  return (
    <>
      <ActiveDocumentList bidResponseId={bidResponseId} />
      <DocumentInput
        onDrop={handleDrop}
        dropzoneCaption={
          <Trans
            ns="bidResponse"
            i18nKey="vault.input.dropzoneCaption"
            components={{ highlight: <BlueHighlight /> }}
          />
        }
      />
    </>
  );
};

function ActiveDocumentList({ bidResponseId }: { bidResponseId: number }) {
  const { data: bidResponse } = useGetBidResponseByIdQuery(bidResponseId);
  const { openPanel } = useDocumentSidePanel({
    documents: bidResponse.documents || [],
  });

  return (
    <>
      {bidResponse.documents?.map(document => (
        <ActiveDocumentItem
          key={document.id}
          document={document}
          bidResponseId={bidResponseId}
          openDocumentSidePanel={openPanel}
        />
      ))}
    </>
  );
}

function ActiveDocumentItem({
  document,
  bidResponseId,
  openDocumentSidePanel,
}: {
  document: BidResponseDocument;
  bidResponseId: number;
  openDocumentSidePanel: (document: BidResponseDocument) => void;
}) {
  const { mutate: updateBidResponseDocumentMutation } =
    useUpdateBidResponseDocumentMutation(bidResponseId);

  const handleTypeChange = (type: BidResponseDocumentType) => {
    updateBidResponseDocumentMutation({
      bidResponseId,
      bidResponseDocumentId: document.id,
      payload: { type },
    });
  };

  return (
    <DocumentItem
      key={document.id}
      document={document}
      onClick={() => {
        openDocumentSidePanel(document);
      }}
    >
      <DocumentItem.Icon />
      <DocumentItem.TypeSelect.BidResponseDocument
        onChange={handleTypeChange}
      />
      <DocumentItem.Name />
      <DocumentItem.PreviewButton displayOnHover />
      <DocumentItem.ArchiveButton.BidResponseDocument displayOnHover />
    </DocumentItem>
  );
}

function ArchivedDocumentList({ bidResponseId }: { bidResponseId: number }) {
  const { data: archivedDocuments } =
    useFindAllArchivedBidResponseDocumentsQuery(bidResponseId);

  return (
    <>
      {archivedDocuments?.documents?.map(document => (
        <ArchivedBidResponseDocumentItem
          key={document.id}
          document={document}
        />
      ))}
    </>
  );
}

function ArchivedBidResponseDocumentItem({
  document,
}: {
  document: BidResponseDocument;
}) {
  return (
    <DocumentItem key={document.id} document={document}>
      <DocumentItem.Icon />
      <DocumentItem.Type.BidResponseDocument />
      <DocumentItem.Name />
      <DocumentItem.Spacer />
      <DocumentItem.RestoreButton.BidResponseDocument displayOnHover />
    </DocumentItem>
  );
}
